
import React, { useState, useRef } from 'react';
import { AgentProfile, VerificationStatus, Sex } from '../types';
import { X, Camera, ShieldCheck, Loader2, CheckCircle2, AlertCircle, User, Building2, Smartphone, Mail, MapPin, Phone } from 'lucide-react';
import { supabase } from '../supabase';

interface ProfileSettingsModalProps {
  agent: AgentProfile;
  onClose: () => void;
  onUpdate: (updatedAgent: Partial<AgentProfile>) => void;
}

const LOCATIONS = [
  'Lagos, Nigeria', 'Abuja, Nigeria', 'Port Harcourt, Nigeria', 'Ibadan, Nigeria',
  'New York, USA', 'London, UK', 'Dubai, UAE', 'Accra, Ghana'
];

export const ProfileSettingsModal: React.FC<ProfileSettingsModalProps> = ({ agent, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    full_name: agent.full_name || '',
    location: agent.location || '',
    birthday: agent.birthday || '',
    sex: agent.sex || 'Other' as Sex,
    business_name: agent.business_name || '',
    bio: agent.bio || '',
    office_address: agent.office_address || '',
    whatsapp: agent.whatsapp || '',
    email: agent.email || '',
    phone: agent.phone || '',
    avatar: agent.avatar
  });

  const handleUpload = async (file: File, bucket: string) => {
    if (!supabase) throw new Error("DB unavailable");
    const fileExt = file.name.split('.').pop();
    const fileName = `${agent.id}_${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return alert("Database connection unavailable.");
    setLoading(true);
    setError(null);

    try {
      let avatarUrl = formData.avatar;

      if (avatarFile) {
        avatarUrl = await handleUpload(avatarFile, 'avatars');
      }

      const updates: Partial<AgentProfile> = {
        ...formData,
        avatar: avatarUrl,
      };

      const { error: dbError } = await supabase
        .from('agents')
        .update(updates)
        .eq('id', agent.id);

      if (dbError) throw dbError;

      onUpdate(updates);
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl animate-in fade-in duration-300 overflow-y-auto">
      <div className="bg-white dark:bg-zinc-950 w-full max-w-2xl rounded-[32px] shadow-2xl overflow-hidden relative flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-zinc-900 flex justify-between items-center sticky top-0 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md z-10">
          <div>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight uppercase">Agent Dashboard</h2>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Manage your personal & professional identity</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-900 rounded-full transition-colors">
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-10 scrollbar-hide">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-2xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-xs font-bold text-red-600 dark:text-red-400 uppercase tracking-widest">{error}</p>
            </div>
          )}

          {/* Avatar Header */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative group">
              <div className="w-28 h-28 rounded-full p-[3px] bg-gradient-to-tr from-yellow-400 via-orange-500 to-pink-500">
                <div className="w-full h-full rounded-full overflow-hidden bg-white dark:bg-black p-[2px]">
                  <img 
                    src={avatarFile ? URL.createObjectURL(avatarFile) : formData.avatar} 
                    className="w-full h-full object-cover rounded-full" 
                    alt="Profile"
                  />
                </div>
              </div>
              <button 
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Camera className="w-6 h-6 text-white" />
              </button>
              <input type="file" ref={avatarInputRef} className="hidden" accept="image/*" onChange={e => setAvatarFile(e.target.files?.[0] || null)} />
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Tap to update business photo</p>
          </div>

          {/* PERSONAL DETAILS SECTION */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 border-b border-gray-100 dark:border-zinc-900 pb-2">
              <User className="w-4 h-4 text-gray-400" />
              <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-900 dark:text-white">Personal Details</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Full Names</label>
                <input required className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm focus:ring-2 focus:ring-black dark:focus:ring-white transition-all border border-transparent hover:border-gray-200 dark:hover:border-zinc-800" value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} placeholder="e.g. Julian Sterling" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Select Location</label>
                <select className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm appearance-none cursor-pointer border border-transparent hover:border-gray-200 dark:hover:border-zinc-800" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})}>
                  <option value="">Choose region...</option>
                  {LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Birthday</label>
                <input type="date" className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent hover:border-gray-200 dark:hover:border-zinc-800" value={formData.birthday} onChange={e => setFormData({...formData, birthday: e.target.value})} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Sex</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['Male', 'Female', 'Other'] as Sex[]).map(s => (
                    <button key={s} type="button" onClick={() => setFormData({...formData, sex: s})} className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${formData.sex === s ? 'bg-black dark:bg-white text-white dark:text-black shadow-lg' : 'bg-gray-50 dark:bg-zinc-900 text-gray-400 border border-transparent hover:border-gray-200 dark:hover:border-zinc-800'}`}>{s}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* COMPANY DETAILS SECTION */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 border-b border-gray-100 dark:border-zinc-900 pb-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-900 dark:text-white">Company Details</h3>
            </div>
            
            <div className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Company Name</label>
                <input required className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent hover:border-gray-200 dark:hover:border-zinc-800" value={formData.business_name} onChange={e => setFormData({...formData, business_name: e.target.value})} placeholder="e.g. Sterling Prime Real Estate" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">About Company</label>
                <textarea required rows={4} className="w-full px-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-medium text-sm resize-none border border-transparent hover:border-gray-200 dark:hover:border-zinc-800" value={formData.bio} onChange={e => setFormData({...formData, bio: e.target.value})} placeholder="Describe your agency's expertise..." />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Office Address</label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input className="w-full pl-12 pr-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent hover:border-gray-200 dark:hover:border-zinc-800" value={formData.office_address} onChange={e => setFormData({...formData, office_address: e.target.value})} placeholder="Full physical office address" />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Direct Call Number</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input required className="w-full pl-12 pr-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent hover:border-gray-200 dark:hover:border-zinc-800" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="e.g. 2348012345678" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">WhatsApp Business Number</label>
                  <div className="relative">
                    <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input required className="w-full pl-12 pr-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent hover:border-gray-200 dark:hover:border-zinc-800" value={formData.whatsapp} onChange={e => setFormData({...formData, whatsapp: e.target.value})} placeholder="e.g. 2348012345678" />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider ml-1">Company Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="email" className="w-full pl-12 pr-5 py-4 bg-gray-50 dark:bg-zinc-900 dark:text-white rounded-2xl outline-none font-bold text-sm border border-transparent hover:border-gray-200 dark:hover:border-zinc-800" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="office@company.com" />
                </div>
              </div>
            </div>
          </div>

          {/* IDENTITY VERIFICATION (FOOTER SECTION) */}
          <div className="p-6 bg-zinc-50 dark:bg-zinc-900 rounded-[32px] border border-zinc-100 dark:border-zinc-800">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white dark:bg-black flex items-center justify-center shadow-sm">
                <ShieldCheck className={`w-6 h-6 ${agent.verification_status === 'verified' ? 'text-green-500' : 'text-blue-500'}`} />
              </div>
              <div className="flex-1">
                <h3 className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-wider">Identity Verification</h3>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Build trust with high-value clients</p>
              </div>
              <button type="button" className="px-5 py-2.5 bg-gray-200 dark:bg-zinc-800 text-gray-500 dark:text-zinc-400 rounded-xl text-[10px] font-black uppercase tracking-widest opacity-50 cursor-not-allowed">Available Soon</button>
            </div>
          </div>
        </form>

        {/* Action Bar */}
        <div className="p-6 border-t border-gray-100 dark:border-zinc-900 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md flex gap-4">
          <button type="button" onClick={onClose} disabled={loading} className="flex-1 py-4 text-xs font-black uppercase tracking-[0.2em] text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">Discard</button>
          <button type="submit" onClick={handleSubmit} disabled={loading} className="flex-[2] bg-black dark:bg-white text-white dark:text-black py-4 rounded-[20px] font-black text-xs uppercase tracking-[0.2em] shadow-2xl flex items-center justify-center gap-3 active:scale-[0.98] transition-all">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
};
